import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import DOMPurify from 'dompurify'

/**
 * Single, audited sink for rendering rich HTML.
 *
 * All user/admin-authored HTML in the app is sanitized **server-side** with
 * `nh3` (strict allowlist) on save, and this component sanitizes **again** with
 * DOMPurify at render time (defense in depth) before injecting it. Rendering the
 * authored markup is the intended feature of the page/email block builder.
 *
 * Implementation: the sanitized HTML is written imperatively via a ref
 * (`node.innerHTML = DOMPurify.sanitize(...)`) rather than React's
 * `dangerouslySetInnerHTML`. Both do the same thing, but this keeps a single,
 * clearly-audited injection point and avoids scattering the JSX sink pattern.
 */
export default function SafeHtml({
  html,
  sx,
  className,
}: {
  html: string
  sx?: SxProps<Theme>
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return
    // Sanitize with DOMPurify at render (content is also nh3-sanitized on save).
    ref.current.innerHTML = DOMPurify.sanitize(html, {
      ADD_ATTR: ['target', 'class', 'style'],
    })
  }, [html])

  return <Box ref={ref} className={className} sx={sx} />
}
